import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CheckLogEntry, CheckResult, StmtResult } from '../../../../services/sqlCheck';
import { formatDateTime } from '../../../../utils/formatters';

/*
 * SQL 편집기 오른쪽의 「검증 결과」「검증 기록」 — 문장마다 상태·줄·사유를 보여 주고, 누르면 편집기에서 그 문장을 고른다.
 * 기록을 고르면 그때의 결과를 같은 목록으로 본다(그때의 SQL 기준이라 지금 편집기 줄과는 다를 수 있다).
 */

const BADGE: Record<string, string> = {
  ok: 'bg-green-100 text-green-700 border-green-200',
  error: 'bg-red-100 text-red-700 border-red-200',
  warn: 'bg-amber-100 text-amber-800 border-amber-200',
  skipped: 'bg-gray-100 text-gray-600 border-gray-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  empty: 'bg-gray-100 text-gray-600 border-gray-200',
};

export const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const { t } = useTranslation();
  return <span className={`shrink-0 px-1.5 py-px rounded border text-[0.625rem] font-semibold ${BADGE[status] || BADGE.skipped}`}>{t(`property:sqlCheck.status.${status}`)}</span>;
};

/** 문장 하나 */
const StmtRow: React.FC<{ s: StmtResult; onSelect?: (s: StmtResult) => void }> = ({ s, onSelect }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const first = (s.text || '').split('\n').find((l) => l.trim()) || '';
  const where = s.endLine > s.line ? t('property:sqlCheck.lineRange', { line: s.line, end: s.endLine }) : t('property:sqlCheck.line', { line: s.line });
  return (
    <div className={`border rounded mb-1 bg-white ${s.status === 'error' ? 'border-red-200' : s.status === 'warn' ? 'border-amber-200' : 'border-gray-200'}`}>
      <button type="button" className="w-full text-left px-2 py-1 hover:bg-blue-50" onClick={() => onSelect?.(s)}>
        <div className="flex items-center gap-1.5 text-[0.6875rem]">
          <span className="font-mono text-gray-400">#{s.index}</span>
          <StatusBadge status={s.status} />
          <span className="text-gray-500">{t(`property:sqlCheck.kind.${s.kind}`)}</span>
          <span className="ml-auto text-gray-400">{where}</span>
        </div>
        <div className="font-mono text-[0.6875rem] text-gray-800 truncate mt-0.5">{first}</div>
        {s.message && <div className="text-[0.6875rem] text-red-700 whitespace-pre-wrap break-words mt-0.5">{s.message}</div>}
        {s.errorLine && <div className="text-[0.625rem] text-red-600">{t('property:sqlCheck.errorAt', { line: s.errorLine })}</div>}
        {s.code && <div className={`text-[0.6875rem] mt-0.5 ${s.status === 'warn' ? 'text-amber-800' : 'text-gray-500'}`}>{t(`property:sqlCheck.reason.${s.code}`, s.params || {})}</div>}
      </button>
      <div className="px-2 pb-1">
        <button type="button" className="text-[0.625rem] text-blue-600 hover:underline" onClick={() => setOpen((v) => !v)}>
          {open ? t('property:sqlCheck.hideText') : t('property:sqlCheck.showText')}
        </button>
        {open && <pre className="mt-1 max-h-48 overflow-auto bg-gray-50 border border-gray-100 rounded p-1.5 text-[0.6875rem] font-mono whitespace-pre-wrap break-words">{s.text}</pre>}
      </div>
    </div>
  );
};

/** 결과 목록 — 지금 결과든 기록이든 같은 모양 */
export const SqlCheckResultList: React.FC<{
  result: Pick<CheckResult, 'statements' | 'summary' | 'status' | 'at' | 'dbType' | 'method'> & { message?: string };
  stale?: boolean;
  onSelect?: (s: StmtResult) => void;
}> = ({ result, stale, onSelect }) => {
  const { t } = useTranslation();
  const [issuesOnly, setIssuesOnly] = useState(false);
  const stmts = (result.statements || []).filter((s) => !issuesOnly || s.status === 'error' || s.status === 'warn');
  const sm = result.summary || { total: 0, ok: 0, error: 0, warn: 0, skipped: 0 };
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <StatusBadge status={result.status} />
        <span className="text-[0.6875rem] text-gray-700">{t('property:sqlCheck.summary', sm)}</span>
      </div>
      <div className="text-[0.625rem] text-gray-400 mb-1">
        {t('property:sqlCheck.meta', { at: formatDateTime(result.at), db: result.dbType || '-', method: t(`property:sqlCheck.method.${result.method || 'none'}`) })}
      </div>
      {result.status === 'failed' && <div className="text-[0.6875rem] text-red-700 mb-1">{t('property:sqlCheck.failed', { message: result.message || '' })}</div>}
      {result.status === 'empty' && <div className="text-[0.6875rem] text-gray-500 mb-1">{t('property:sqlCheck.empty')}</div>}
      {stale && <div className="text-[0.6875rem] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-1 mb-1">{t('property:sqlCheck.stale')}</div>}
      {(sm.error > 0 || sm.warn > 0) && (
        <div className="flex gap-1 mb-1 text-[0.625rem]">
          {[false, true].map((v) => (
            <button key={String(v)} type="button" onClick={() => setIssuesOnly(v)}
              className={`px-1.5 py-px rounded border ${issuesOnly === v ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300'}`}>
              {v ? t('property:sqlCheck.filterIssues') : t('property:sqlCheck.filterAll')}
            </button>
          ))}
        </div>
      )}
      {stmts.map((s) => <StmtRow key={s.index} s={s} onSelect={onSelect} />)}
    </div>
  );
};

/** 검증 기록 목록 */
export const SqlCheckLogList: React.FC<{
  items: CheckLogEntry[] | null;
  loading: boolean;
  error: string;
  onRefresh: () => void;
  onPick: (e: CheckLogEntry) => void;
}> = ({ items, loading, error, onRefresh, onPick }) => {
  const { t } = useTranslation();
  return (
    <div>
      <div className="flex justify-end mb-1">
        <button type="button" className="px-1.5 py-0.5 text-[0.625rem] rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700" onClick={onRefresh}>{t('property:sqlCheck.log.refresh')}</button>
      </div>
      {loading && <div className="text-xs text-gray-400 px-1 py-2">{t('property:sqlCheck.log.loading')}</div>}
      {error && <div className="text-xs text-red-700 px-1 py-2">{t('property:sqlCheck.log.loadFailed', { message: error })}</div>}
      {!loading && !error && items && items.length === 0 && <div className="text-xs text-gray-400 px-1 py-2">{t('property:sqlCheck.log.empty')}</div>}
      {(items || []).map((e) => {
        const sm = e.summary;
        return (
          <button key={e.id} type="button" className="w-full text-left px-2 py-1 mb-1 rounded border border-gray-200 bg-white hover:bg-blue-50" onClick={() => onPick(e)}>
            <div className="flex items-center gap-1.5">
              <StatusBadge status={e.status} />
              <span className="text-[0.6875rem] text-gray-700 truncate">
                {t('property:sqlCheck.log.row', { at: formatDateTime(e.at), user: e.user || '-', trigger: t(`property:sqlCheck.log.trigger.${e.trigger || 'manual'}`) })}
              </span>
            </div>
            {sm && sm.total !== undefined && <div className="text-[0.625rem] text-gray-500 mt-0.5">{t('property:sqlCheck.summary', sm)}</div>}
            {e.message && <div className="text-[0.625rem] text-red-700 truncate">{e.message}</div>}
          </button>
        );
      })}
    </div>
  );
};
